import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  MenuItem,
  Paper
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import DownloadIcon from '@mui/icons-material/Download';
import { useLocation } from 'react-router-dom';

interface Payment {
  id: string;
  orderId: string;
  planName: string;
  amount: number;
  date: string;
  status: 'success' | 'failed' | 'pending';
  paymentMethod?: string;
  invoice?: string;
}

const PaymentSummary: React.FC = () => {
  const location = useLocation();
  const recentPayment = location.state?.payment;

  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');

  // Mock payment history
  const payments: Payment[] = [
    ...(recentPayment ? [{
      id: recentPayment.id || 'pay_' + Date.now(),
      orderId: recentPayment.orderId || 'order_' + Date.now(),
      planName: recentPayment.planName || 'Pro',
      amount: recentPayment.amount || 29,
      date: recentPayment.date || new Date().toISOString(),
      status: recentPayment.status || 'success',
      paymentMethod: 'Razorpay',
      invoice: 'INV-' + Date.now()
    }] : []),
    {
      id: 'pay_abc123xyz789',
      orderId: 'order_xyz123',
      planName: 'Pro',
      amount: 29,
      date: '2025-01-15T10:30:00Z',
      status: 'success',
      paymentMethod: 'Razorpay',
      invoice: 'INV-2025-001'
    },
    {
      id: 'pay_def456uvw012',
      orderId: 'order_uvw456',
      planName: 'Pro',
      amount: 29,
      date: '2024-12-15T14:20:00Z',
      status: 'success',
      paymentMethod: 'Razorpay',
      invoice: 'INV-2024-012'
    },
    {
      id: 'pay_ghi789rst345',
      orderId: 'order_rst789',
      planName: 'Enterprise',
      amount: 99,
      date: '2024-11-15T09:15:00Z',
      status: 'failed',
      paymentMethod: 'Razorpay',
      invoice: ''
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon fontSize="small" />;
      case 'failed':
        return <ErrorIcon fontSize="small" />;
      case 'pending':
        return <PendingIcon fontSize="small" />;
      default:
        return undefined;
    }
  };

  const getStatusColor = (status: string): "success" | "error" | "warning" | "default" => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDownloadInvoice = (payment: Payment) => {
    // In a real app, this would download the invoice PDF
    alert(`Downloading invoice: ${payment.invoice}`);
  };

  const filteredPayments = payments.filter(payment => {
    if (filterStatus !== 'all' && payment.status !== filterStatus) return false;
    if (filterMonth !== 'all') {
      const paymentDate = new Date(payment.date);
      const paymentMonth = paymentDate.toISOString().substring(0, 7); // YYYY-MM
      if (paymentMonth !== filterMonth) return false;
    }
    return true;
  });

  const totalSpent = payments
    .filter(p => p.status === 'success')
    .reduce((sum, p) => sum + p.amount, 0);

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight={700}>
        Payment Summary
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        View your payment history and download invoices
      </Typography>

      {/* Recent Payment Success Alert */}
      {recentPayment && (
        <Card sx={{ mb: 4, bgcolor: '#e8f5e9', border: '1px solid #4caf50' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h6" fontWeight={700} color="success.main">
                  Payment Successful!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your {recentPayment.planName} plan is now active
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Payment ID</Typography>
                <Typography variant="body1" fontWeight={600}>{recentPayment.id}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Amount Paid</Typography>
                <Typography variant="body1" fontWeight={600}>₹{recentPayment.amount}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Date</Typography>
                <Typography variant="body1" fontWeight={600}>{formatDate(recentPayment.date)}</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography color="text.secondary" gutterBottom variant="body2">
              Total Spent
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              ₹{totalSpent}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography color="text.secondary" gutterBottom variant="body2">
              Total Payments
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              {payments.length}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography color="text.secondary" gutterBottom variant="body2">
              Successful
            </Typography>
            <Typography variant="h4" fontWeight={700} color="success.main">
              {payments.filter(p => p.status === 'success').length}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography color="text.secondary" gutterBottom variant="body2">
              Failed
            </Typography>
            <Typography variant="h4" fontWeight={700} color="error.main">
              {payments.filter(p => p.status === 'failed').length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" gutterBottom>
            Filter Payments
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <TextField
              select
              label="Status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              sx={{ minWidth: 200 }}
              size="small"
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </TextField>
            <TextField
              select
              label="Month"
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              sx={{ minWidth: 200 }}
              size="small"
            >
              <MenuItem value="all">All Months</MenuItem>
              <MenuItem value="2025-01">January 2025</MenuItem>
              <MenuItem value="2024-12">December 2024</MenuItem>
              <MenuItem value="2024-11">November 2024</MenuItem>
            </TextField>
          </Box>
        </CardContent>
      </Card>

      {/* Payment History Table */}
      <Card>
        <CardContent sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          <Typography variant="h6" gutterBottom sx={{ px: { xs: 1, sm: 0 } }}>
            Payment History
          </Typography>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer component={Paper} elevation={0}>
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Payment ID</strong></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Order ID</strong></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Plan</strong></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }} align="right"><strong>Amount</strong></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Date</strong></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Status</strong></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }} align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPayments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No payments found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPayments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <code style={{ fontSize: '0.85rem' }}>{payment.id.substring(0, 20)}...</code>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <code style={{ fontSize: '0.85rem' }}>{payment.orderId}</code>
                        </TableCell>
                        <TableCell>
                          <Chip label={payment.planName} size="small" />
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <strong>₹{payment.amount}</strong>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(payment.date)}</TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(payment.status)}
                            label={payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                            color={getStatusColor(payment.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {payment.status === 'success' && payment.invoice && (
                            <Button
                              size="small"
                              startIcon={<DownloadIcon />}
                              onClick={() => handleDownloadInvoice(payment)}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              Invoice
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PaymentSummary;

